import React from "react";
import { styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Hidden, Switch, Grid, Box } from "@mui/material";
import Clock from "./Clock";
import ConfirmModal from "../Common/CommonModal";
import { removeAuthObjects, toastError, toastSuccess } from "../../utils";
import appLogo from "../../assets/img/Fronic_logo_black.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as commonAction from "../Common/actions";
import FullScreenLoader from "../Common/FullScreenLoader";

//icons
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import DashboardIcon from "@mui/icons-material/Dashboard";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import ContactsIcon from "@mui/icons-material/Contacts";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CustomAddModal from "../Common/CustomAddModal";
import GavelIcon from "@mui/icons-material/Gavel";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import HearingIcon from "@mui/icons-material/Hearing";
import MusicNote from "@mui/icons-material/MusicNote";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import CampaignIcon from "@mui/icons-material/Campaign";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

const drawerWidth = 270;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: 300,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: 300,
    }),
  }),
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: 300,
      }),
    },
  }),
  ...(!open && {
    '& .MuiDrawer-paper': {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: 300,
      }),
      overflowX: 'hidden',
      width: theme.spacing(7) + 1,
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9) + 1,
      },
    },
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
}));

const initialListItems = [
  {
    label: "Dashboard",
    icon: <DashboardIcon />,
    path: "/",
  },
  {
    label: "Information",
    icon: <InfoIcon />,
    path: "/information",
  },
  {
    label: "Review Releases",
    icon: <NewReleasesIcon />,
    path: `/releases/review`,
  },
  {
    label: "Approved Releases",
    icon: <NewReleasesIcon />,
    path: "/release/approved",
  },
  {
    label: "Rejected Releases",
    icon: <NewReleasesIcon />,
    path: "/release/rejected",
  },
  {
    label: "Takendown Releases",
    icon: <RemoveCircleOutlineIcon />,
    path: "/release/takendown",
  },
  {
    label: "Video Releases",
    icon: <SlowMotionVideoIcon />,
    path: "/video_releases/review",
  },
  {
    label: "Video History",
    icon: <VoiceChatIcon />,
    path: "/video_releases/history",
  },
  {
    label: "UGC Claims",
    icon: <GavelIcon />,
    path: "/ugc_claims",
  },
  {
    label: "Profile Linking",
    icon: <SupervisorAccountIcon />,
    path: "/profile_linking",
  },
  {
    label: "Playlist Pitching",
    icon: <HearingIcon />,
    path: "/playlist_pitching",
  },
  {
    label: "Lyrics Distribution",
    icon: <MusicNote />,
    path: "/lyrics_distribution",
  },
  {
    label: "Whitelisting",
    icon: <PlaylistPlayIcon />,
    path: "/whitelisting",
  },
  {
    label: "Promotions",
    icon: <CampaignIcon />,
    path: "/promotions",
  },
    {
    label: "Admin Actions",
    icon: <ManageAccountsIcon />,
    path: "/user_actions",
  },
  {
    label: "User Details",
    icon: <RecentActorsIcon />,
    path: "/user_details",
  },
  {
    label: "Artists",
    icon: <ContactsIcon />,
    path: "/artists",
  },
  {
    label: "Report Requests",
    icon: <AssignmentIcon />,
    path: "/report_requests",
  },
  {
    label: "Tickets",
    icon: <ConfirmationNumberIcon />,
    path: "/tickets",
  },
  {
    label: "Requested Payouts",
    icon: <MonetizationOnIcon />,
    path: "/payout",
  },
  {
    label: "Notifications",
    icon: <NotificationsActiveIcon />,
    path: "/notifications",
  },
  {
    label: "Invite",
    icon: <PersonAddIcon />,
    path: "/invite",
  },
  {
    label: "Logout",
    icon: <MeetingRoomIcon />,
    path: "/login",
  },
];

export default function MainContainer(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [confirmOpen, setConfimOpen] = React.useState(false);
  const [listItems, setListItems] = React.useState([...initialListItems]);
  const [awayStatus, setAwayStatus] = React.useState(false);
  const [awayText, setAwayText] = React.useState("");
  const [openAwayText, setOpenAwayText] = React.useState(false);

  React.useEffect(() => {
    commonAction
      .countMeterAdmin()
      .then((res) => {
        let updatedList = [];
        const {
          in_review = 0,
          open_tickets = 0,
          reports_requested = 0,
          withdrawl_requested = 0,
          ugc_claim = 0,
          profile_linking = 0,
          pitch_playlist = 0,
          distribute_lyrics = 0,
          video_releases = 0,
          whitelisting = 0,
        } = res.body || {};
        for (let i = 0; i < initialListItems.length; i++) {
          const item = initialListItems[i];
          if (item.label === "Review Releases")
            updatedList.push({
              ...item,
              label: `${item.label} (${in_review})`,
            });
          else if (item.label === "Tickets")
            updatedList.push({
              ...item,
              label: `${item.label} (${open_tickets})`,
            });
          else if (item.label === "Report Requests")
            updatedList.push({
              ...item,
              label: `${item.label} (${reports_requested})`,
            });
          else if (item.label === "Requested Payouts")
            updatedList.push({
              ...item,
              label: `${item.label} (${withdrawl_requested})`,
            });
          else if (item.label === "UGC Claims")
            updatedList.push({
              ...item,
              label: `${item.label} (${ugc_claim})`,
            });
          else if (item.label === "Profile Linking")
            updatedList.push({
              ...item,
              label: `${item.label} (${profile_linking})`,
            });
          else if (item.label === "Playlist Pitching")
            updatedList.push({
              ...item,
              label: `${item.label} (${pitch_playlist})`,
            });
          else if (item.label === "Lyrics Distribution")
            updatedList.push({
              ...item,
              label: `${item.label} (${distribute_lyrics})`,
            });
          else if (item.label === "Video Releases")
            updatedList.push({
              ...item,
              label: `${item.label} (${video_releases})`,
            });
          else if (item.label === "Whitelisting")
            updatedList.push({
              ...item,
              label: `${item.label} (${whitelisting})`,
            });
          else updatedList.push({ ...item });
        }
        setListItems(updatedList);
      })
      .catch(() => {});
    commonAction.checkAwayStatus().then((res) => {
      setAwayStatus(
        res && res.body && res.body.status && res.body.status === "yes"
          ? true
          : false || false
      );
      setAwayText((res && res.body && res.body.text) || "");
    });
  }, []);

  React.useEffect(() => {
    window.addEventListener("resize", (event) => {
      if (document.body.clientWidth < 700) {
        if (open) setOpen(false);
      } else {
        if (!open) setOpen(true);
      }
    });
    return () => {
      window.removeEventListener("resize", () => {});
    };
  }, [open]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const header = () => {
    const path = location.pathname;
    switch (path) {
      case "/":
        return "Dashboard";
      case "/information":
        return "Information";
      case "/releases/review":
        return "Review Releases";
      case "/release/approved":
        return "Approved Releases";
      case "/release/rejected":
        return "Rejected Releases";
      case "/user_details":
        return "User Details";

      case "/user_actions":
        return "Admin Actions";


      case "/report_requests":
        return "Report Requests";
      case "/tickets":
        return "Tickets";
      case "/payout":
        return "Requested Payouts";
      case "/notifications":
        return "Notifications";
      case "/ugc_claims":
        return "UGC Claims";
      case "/profile_linking":
        return "Profile Linking";
      case "/playlist_pitching":
        return "Playlist Pitching";
      default: {
        return "";
      }
    }
  };

  const handleConfirmClickOpen = () => {
    setConfimOpen(true);
  };

  const handleConfirmClose = () => {
    setConfimOpen(false);
  };

  const onConfirmLogout = () => {
    onSetLoader(true);
    removeAuthObjects();
    setTimeout(() => {
      onSetLoader(false);
      return navigate("/login");
    }, 1000);
  };

  const onChangeAwayStatus = (checked = false) => {
    onSetLoader(true);
    commonAction
      .changeAwayStatusAdmin({
        status: checked ? "yes" : "no",
        text: awayText || "",
      })
      .then((res) => {
        onSetLoader(false);
        setAwayStatus(checked);
        setOpenAwayText(false);
        toastSuccess(res.message || "Status changed successfully");
      })
      .catch((err) => {
        onSetLoader(false);
        toastError(err.message);
      });
  };

  const onSetLoader = (action) => {
    setLoader(action);
  };

  const onSetAwayText = (e) => {
    if (!awayStatus) {
      setOpenAwayText(true);
    } else {
      onChangeAwayStatus(false);
    }
  };

  return (
    <>
      <FullScreenLoader open={loader} />

      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                width: '100%',
                justifyContent: 'space-between',
                display: 'flex',
              }}
            >
              <Typography variant="h6" noWrap sx={{ alignSelf: 'center' }}>
                {header()}
              </Typography>
              <Hidden only={["xs"]}>
                <Box
                  sx={{
                    alignSelf: 'center',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <p>({awayStatus ? "Away" : "Available"})</p>
                  <Switch
                    checked={awayStatus}
                    style={{ color: "#65ffcd" }}
                    onChange={onSetAwayText}
                  />
                  <p
                    style={{
                      paddingRight: "23px",
                      marginRight: "23px",
                      fontSize: "18px",
                      borderRight: "1px solid #d8d8d8",
                    }}
                  >
                    WELCOME, ADMIN.
                  </p>
                  <Clock />
                </Box>
              </Hidden>
              <Hidden only={["sm", "md", "lg", "xl"]}>
                <Box
                  sx={{
                    alignSelf: 'center',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Switch
                    checked={awayStatus}
                    style={{ color: "#65ffcd" }}
                    onChange={onSetAwayText}
                  />
                </Box>
              </Hidden>
            </Box>
          </Toolbar>
        </AppBar>
        <StyledDrawer variant="permanent" open={open}>
          <DrawerHeader>
            <Box
              sx={{
                width: '100%',
                height: '40px',
                display: 'flex',
              }}
            >
              <img
                style={{
                  margin: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
                src={appLogo}
                alt=""
                onClick={() => navigate("/")}
              />
            </Box>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {listItems.map((text, index) => {
              if (text.label === "Logout") {
                return (
                  <ListItem
                    style={{
                      backgroundColor:
                        location.pathname === text.path ? "#dedede" : "",
                    }}
                    button
                    key={text.label}
                    onClick={handleConfirmClickOpen}
                  >
                    <ListItemIcon style={{ minWidth: 40 }}>
                      {text.icon}
                    </ListItemIcon>
                    <ListItemText primary={text.label} />
                  </ListItem>
                );
              } else {
                return (
                  <Link
                    className="link-color-black"
                    key={text.label}
                    to={text.path}
                  >
                    <ListItem
                      style={{
                        backgroundColor:
                          location.pathname === text.path
                            ? "#dedede"
                            : "",
                      }}
                      button
                    >
                      <ListItemIcon style={{ minWidth: 40 }}>
                        {text.icon}
                      </ListItemIcon>
                      <ListItemText primary={text.label} />
                    </ListItem>
                  </Link>
                );
              }
            })}
          </List>
        </StyledDrawer>
        <Main>
          <DrawerHeader />
          {React.cloneElement(props.children, { setLoader: onSetLoader })}
        </Main>
        <ConfirmModal
          message={`Are you sure you want to Logout?`}
          open={confirmOpen}
          title={`Confirm Logout`}
          onSubmitConfirm={onConfirmLogout}
          handleClose={handleConfirmClose}
        ></ConfirmModal>

        <CustomAddModal
          open={openAwayText}
          saveLabel="Set Away"
          title="Add Away Text"
          handleClose={() => setOpenAwayText(false)}
          onSubmitConfirm={() => onChangeAwayStatus(true)}
        >
          <Grid container style={{ height: "330px", display: "block" }}>
            <Grid item xs={12} style={{ height: "calc(100% - 60px)" }}>
              <textarea
                className="text-area-input"
                placeholder="Please add a text"
                value={awayText}
                onChange={(e) => setAwayText(e.target.value)}
              />
            </Grid>
          </Grid>
        </CustomAddModal>
      </Box>
    </>
  );
}
